import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, TimeoutError } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { StateService } from './state.service';
import { State } from '../models/state.model';
import { TranslateService } from '@ngx-translate/core';
import { AppInitService } from './app-init.service';
import { AppState } from '../store/app.state';
import { NavigationHelper } from '../helpers/navigation.helper';
import { RouteHelper } from '../helpers/route.helper';

@Injectable({
  providedIn: 'root'
})
export class SsoService implements OnDestroy {
  private _ssoAuthenticated = new BehaviorSubject<boolean>(false);
  public ssoAuthenticated$ = this._ssoAuthenticated.asObservable();

  private readonly apiUrl = environment.config.rest_end_point + '/api/jwt';
  private state: State;

  private initialized = false;
  private readonly boundMessageHandler = (event: MessageEvent) => this.handleMessage(event);

  private processing = false;
  private lastJwtFingerprint: string | null = null;

  private ssov = 'v1';

  private timeoutHandle: any = null;
  private readonly tokenWaitMs = 2000; // 2 seconds - short wait for new JWT

  // OPTIONAL: allowlist
  // Példa: environment.config.sso_allowed_origins = ['https://host1.com', 'https://host2.com']
  private readonly allowedOrigins: string[] = (environment as any)?.config?.sso_allowed_origins ?? [];

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private stateService: StateService,
    private router: Router,
    private ngZone: NgZone,
    private translate: TranslateService,
    private appInitService: AppInitService,
    private appState: AppState
  ) {
    this.state = this.stateService.state;
  }

  ngOnDestroy(): void {
    // Cleanup timeout
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }
    
    // Remove message listener
    if (this.initialized) {
      window.removeEventListener('message', this.boundMessageHandler);
    }
  }

  public init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    if (!NavigationHelper.isIframe()) {
      // Non-iframe mode: ensure state is sane
      this.appState.iframeLoginMode.value = false;
      this.appState.ssoStatus.value = 'idle';
      this.appState.ssoError.value = null;
      return;
    }

    console.log('SSO: Iframe mode detected, setting up listeners');
    this.appState.iframeLoginMode.value = true;

    // Always start in initializing state (shows loading animation)
    this.appState.ssoStatus.value = 'initializing';

    // Remove the early listener from index.html
    if ((window as any).__ssoCaptureFn) {
      window.removeEventListener('message', (window as any).__ssoCaptureFn);
      (window as any).__ssoCaptureFn = null;
    }

    // Attach listener for future messages (stable reference)
    window.addEventListener('message', this.boundMessageHandler, false);

    // Process early captured message if any
    const captured = (window as any).__ssoCapturedMessage;
    if (captured?.data?.jwt) {
      console.log('SSO: Found early captured token, processing now...', { origin: captured.origin });
      (window as any).__ssoCapturedMessage = null;
      this.handleParsedMessage(captured.data, captured.origin, captured.source);
    } else {
      console.log('SSO: No early token found, listening for postMessage');
    }

    // Short timeout: if no JWT arrives within 2 seconds, fall back to local token
    // This provides quick loading for iframe refresh while still waiting for new JWTs
    // IMPORTANT: We continue listening for new JWTs even after timeout (for user switches)
    this.timeoutHandle = setTimeout(() => {
      if (this.appState.ssoStatus.value === 'initializing' && !this.processing) {
        const isReload = NavigationHelper.isReload();
        const hasValidToken = this.authService.isAuthenticated();
        const isUserSwitchReload = sessionStorage.getItem('sso_user_switch_reload') === 'true';
        
        // Clear the flag after reading
        if (isUserSwitchReload) {
          sessionStorage.removeItem('sso_user_switch_reload');
        }
        
        // Use local token if either:
        // 1. User pressed F5/refresh (isReload=true) - safe to use existing token
        // 2. Fast-track reload after user switch (isUserSwitchReload=true) - new token was just saved
        if ((isReload || isUserSwitchReload) && hasValidToken) {
          if (isReload) {
            console.log('SSO: Refresh detected (F5), no new JWT received within 2 seconds, using existing local token');
            this.appState.isLoggedIn.value = true;
            this.appState.ssoStatus.value = 'success';
            // User data already loaded, just unlock UI
            this.navigateIfPrivacyPolicyNeeded();
          } else {
            // User switch reload - need to load the new user's data
            console.log('SSO: User switch reload detected, using newly saved token');
            this.appState.isLoggedIn.value = true;
            
            this.ngZone.run(() => {
              // Load user profile and app data for the new user
              this.loadUserAndAppData(() => {
                this.appState.ssoStatus.value = 'success';
                this.navigateIfPrivacyPolicyNeeded();
              }, 'SSO User switch');
            });
          }
          // Note: If token is expired, first API call will fail with 401 and handle it
        } else {
          // Either no valid token, or navigation without reload/user-switch
          // For security: don't use old token if user switch occurred but host didn't send new JWT
          if (!isReload && hasValidToken) {
            console.warn('SSO: Navigation detected with old token. Clearing for security (possible user switch without new JWT).');
            this.authService.logOut();
          }
          console.warn('SSO: Timeout reached, no JWT received from host');
          this.appState.ssoStatus.value = 'error';
          this.appState.ssoError.value = this.translate.instant('SSO.TIMEOUT_ERROR');
        }
      }
    }, this.tokenWaitMs);
  }

  // OPTIONAL: allowlist

  private handleMessage(event: MessageEvent): void {
    if (!event) return;

    console.log('PostMessage: Received a message', event);

    // event.data may be object or JSON string
    const parsed = this.tryParseMessageData(event.data);
    if (!parsed) {
      console.log('SSO: Message data could not be parsed, ignoring');
      return;
    }

    // Optional origin allowlist (recommended)
    if (!this.isOriginAllowed(event.origin)) {
      console.warn('SSO: Origin not in allowlist, rejecting message:', event.origin);
      return;
    }

    this.handleParsedMessage(parsed, event.origin, event.source);
  }

  private handleParsedMessage(data: any, origin: string, source: MessageEventSource | null): void {
    if (!data?.jwt) {
      // ignore non-SSO messages
      console.log('SSO: Message has no jwt field, ignoring');
      return;
    }

    // If token arrives late (even after timeout error), switch back to validating.
    this.appState.ssoError.value = null;
    this.appState.ssoStatus.value = 'validating';

    // Dedupe & lock
    const fp = this.fingerprintJwt(data.jwt);
    if (this.processing) {
      // if same token is currently processing, ignore
      if (this.lastJwtFingerprint && this.lastJwtFingerprint === fp) {
        console.log('SSO: Same token already being processed, ignoring duplicate');
        return;
      }
      // otherwise ignore as well to avoid parallel logins
      console.log('SSO: Another token is being processed, ignoring new token to avoid race');
      return;
    }

    if (this.lastJwtFingerprint && this.lastJwtFingerprint === fp) {
      // already processed
      console.log('SSO: Token already processed previously, ignoring');
      return;
    }

    this.processing = true;
    this.lastJwtFingerprint = fp;

    // Early check: compare host token 'user' claim with current local username
    try {
        const hostTokenData = this.authService.jwtHelper.decodeToken(data.jwt);
        const hostUserName = hostTokenData?.user;
        const currentUserName = this.getCurrentTokenUserName();
        
        if (currentUserName && hostUserName && currentUserName !== hostUserName) {
            console.log('SSO: User switch detected early from host token!', { current: currentUserName, incoming: hostUserName });
        }
    } catch (e) {
        console.warn('SSO: Could not decode host token for early comparison');
    }

    console.log('SSO: JWT token received, processing...', { fingerprint: fp, length: data.jwt.length });
    this.verifyToken(data.jwt, origin);
  }

  private tryParseMessageData(data: any): any | null {
    if (!data) return null;

    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return null;
      }
    }

    if (typeof data === 'object') {
      return data;
    }

    return null;
  }

  private isOriginAllowed(origin: string): boolean {
    if (!origin) return false;

    // If you have no allowlist configured, allow all (current behavior)
    if (!this.allowedOrigins || this.allowedOrigins.length === 0) {
      return true;
    }

    return this.allowedOrigins.includes(origin);
  }

  private fingerprintJwt(jwt: string): string {
    // Cheap fingerprint without crypto: length + first/last chars
    // Enough for dedupe, not for security.
    if (!jwt) return 'empty';
    const first = jwt.substring(0, 12);
    const last = jwt.substring(Math.max(0, jwt.length - 12));
    return `${jwt.length}:${first}:${last}`;
  }

  private verifyToken(token: string, origin: string): void {
    this.http
      .post<{ valid: boolean; token?: string; error?: string }>(`${this.apiUrl}/verify`, { token, origin })
      .pipe(
        timeout(10000) // 10 second timeout for backend verification
      )
      .subscribe({
        next: (response) => {
          if (response.valid && response.token) {
            console.log('SSO: Backend verification successful, proceeding with login');
            const currentUserName = this.getCurrentTokenUserName();

            this.ngZone.run(() => {
              const tokenData = this.authService.jwtHelper.decodeToken(response.token);
              const newUserName = tokenData?.unique_name; // unique_name claim from TokenBuilder.cs

              console.log('SSO: Comparing usernames...', { current: currentUserName, incoming: newUserName });

              // If user changed (and we had a user), skip full initialization and reload immediately
              if (currentUserName && newUserName && currentUserName !== newUserName) {
                console.log('SSO: Fast-track reload: Different user detected in JWT');
                this.authService.tokenService.saveToken(response.token);
                // Set flag to indicate this is a legitimate user switch reload with new token
                sessionStorage.setItem('sso_user_switch_reload', 'true');
                window.location.href = '/';
                return;
              }

              console.log('SSO: Calling loginWithToken...');
              this.authService.loginWithToken(response.token).subscribe({
                next: (user) => {
                  console.log('SSO: loginWithToken result:', user ? 'User found' : 'User is NULL');
                  this.appState.iframeLoginMode.value = true;
                  this._ssoAuthenticated.next(true);

                  this.appInitService.load().subscribe({
                    next: () => {
                      console.log('SSO: Global data loaded');

                      // Fallback reload if fast-track was skipped but user still differs (unlikely)
                      console.log('SSO: Checking for user change...', { 
                        user: user, 
                        userName: user?.userName,
                        currentUserName: currentUserName,
                        areEqual: currentUserName === user?.userName,
                        userExists: !!user,
                        currentUserNameExists: !!currentUserName
                      });
                      
                      if (user && currentUserName && currentUserName !== user.userName) {
                        console.log('SSO: User changed (fallback), reloading page');
                        window.location.href = '/';
                        return;
                      }

                      console.log('SSO: Global data loaded (User same as local or fresh login)');
                      this.appState.ssoStatus.value = 'success';
                      this.processing = false; // allow future tokens (e.g. user switch)
                      // Check if privacy policy needs to be accepted before unlocking UI
                      this.navigateIfPrivacyPolicyNeeded();
                    },
                    error: (err) => {
                      console.error('SSO: Global data load failed', err);
                      this.appState.ssoStatus.value = 'error';
                      this.appState.ssoError.value = this.translate.instant('SSO.INIT_LOAD_FAILED');
                      this.processing = false; // allow retry token
                    }
                  });
                },
                error: (err) => {
                  console.error('SSO Login failed', err);
                  this.appState.ssoStatus.value = 'error';
                  this.appState.ssoError.value = this.translate.instant('SSO.LOGIN_FAILED');
                  this.processing = false; // allow retry token
                }
              });
            });
          } else {
            console.error('SSO: Backend returned invalid token response:', response);
            this.appState.ssoStatus.value = 'error';
            this.appState.ssoError.value = response.error || this.translate.instant('SSO.INVALID_TOKEN');
            this.processing = false; // allow retry token
          }
        },
        error: (err) => {
          if (err instanceof TimeoutError) {
            console.error('SSO: Backend verification timed out after 10 seconds');
            this.appState.ssoStatus.value = 'error';
            this.appState.ssoError.value = this.translate.instant('SSO.VERIFICATION_TIMEOUT');
          } else {
            console.error('SSO Verification failed', err);
            this.appState.ssoStatus.value = 'error';
            this.appState.ssoError.value = this.translate.instant('SSO.VERIFICATION_ERROR');
          }
          this.processing = false; // allow retry token
        }
      });
  }

  private getCurrentTokenUserName(): string | null {
    try {
      const token = this.authService.tokenService.getToken();
      if (!token) return null;
      const decoded = this.authService.jwtHelper.decodeToken(token);
      return decoded?.unique_name; // Return unique_name from our system token
    } catch (e) {
      return null;
    }
  }

  /**
   * Common method to load user profile and app data
   * Used by both normal SSO flow and user switch reload scenarios
   */
  private loadUserAndAppData(onSuccess: () => void, context: string = 'SSO'): void {
    this.authService.loadUser().subscribe({
      next: () => {
        this.appInitService.load().subscribe({
          next: () => {
            console.log(`${context}: User and app data loaded successfully`);
            onSuccess();
          },
          error: (err) => {
            console.error(`${context}: App data load failed`, err);
            this.appState.ssoStatus.value = 'error';
            this.appState.ssoError.value = this.translate.instant('SSO.INIT_LOAD_FAILED');
            this.processing = false;
          }
        });
      },
      error: (err) => {
        console.error(`${context}: User profile load failed`, err);
        this.appState.ssoStatus.value = 'error';
        this.appState.ssoError.value = this.translate.instant('SSO.LOGIN_FAILED');
        this.processing = false;
      }
    });
  }

  /**
   * Checks if the current user needs to accept the privacy policy.
   * If so, navigates to /privacy-policy.
   */
  private navigateIfPrivacyPolicyNeeded(): void {
    const user = this.authService.getUserProfile();
    if (user && !user.privacyPolicyAccepted) {
      console.log('SSO: Privacy policy not accepted, navigating to /privacy-policy');
      this.router.navigate(['/privacy-policy']);
    }
  }
}